import React, { useEffect, useRef } from 'react';
import type { User, CandidateViewType } from '../types';
import { CandidateLayout } from '../components/CandidateLayout';
import { MyPathways } from './candidate/MyPathways';
import { PathwayView } from './candidate/PathwayView';
import { TheoryBlockView } from './candidate/blocks/TheoryBlockView';
import { OjtBlockView } from './candidate/blocks/OjtBlockView';
import { PracticalBlockView } from './candidate/blocks/PracticalBlockView';
import { AssessmentBlockView } from './candidate/blocks/AssessmentBlockView';
import { UploadBlockView } from './candidate/blocks/UploadBlockView';
import { ApiBlockView } from './candidate/blocks/ApiBlockView';
import { AuthorisationBlockView } from './candidate/blocks/AuthorisationBlockView';
import { PlaceholderBlockView } from './candidate/blocks/PlaceholderBlockView';
import { OjtSessionFlow } from './candidate/ojt/OjtSessionFlow';
import { OjtAgentChat } from './candidate/ojt/OjtAgentChat';
import { useTranslation } from 'react-i18next';

interface CandidateAppProps {
  user: User;
  currentView: CandidateViewType;
  onViewChange: (view: CandidateViewType) => void;
  selectedPathwayId: string | null;
  selectedBlockId: string | null;
  onSelectPathway: (pathwayId: string) => void;
  onSelectBlock: (blockId: string, blockType?: string) => void;
  onStartOjtSession: (blockId: string) => void;
  onLogout: () => void;
  onSwitchToAdmin?: () => void;
  isAdmin: boolean;
}

export const CandidateApp: React.FC<CandidateAppProps> = ({
  user,
  currentView,
  onViewChange,
  selectedPathwayId,
  selectedBlockId,
  onSelectPathway,
  onSelectBlock,
  onStartOjtSession,
  onLogout,
  onSwitchToAdmin,
  isAdmin,
}) => {
  const { t } = useTranslation();
  const [selectedBlock, setSelectedBlock] = React.useState<any>(null);
  const [selectedPathway, setSelectedPathway] = React.useState<any>(null);
  const [activeSessionId, setActiveSessionId] = React.useState<string | null>(null);

  const getTitle = (): string => {
    switch (currentView) {
      case 'my-pathways': return t('candidate.nav.myPathways');
      case 'pathway-detail': return selectedPathway?.title || t('candidate.pathway.title');
      case 'block-detail': return selectedBlock?.name || '';
      case 'ojt-session': return 'OJT Session';
      default: return 'SoloReady';
    }
  };

  const showBack = currentView !== 'my-pathways';

  const handleBack = () => {
    switch (currentView) {
      case 'pathway-detail':
        onViewChange('my-pathways');
        break;
      case 'block-detail':
        onViewChange('pathway-detail');
        break;
      case 'ojt-session':
        onViewChange('block-detail');
        break;
      default:
        onViewChange('my-pathways');
    }
  };

  const renderContent = () => {
    switch (currentView) {
      case 'my-pathways':
        return (
          <MyPathways
            user={user}
            onSelectPathway={(pathway) => {
              setSelectedPathway(pathway);
              onSelectPathway(pathway.pathwayId);
            }}
          />
        );
      case 'pathway-detail':
        return selectedPathwayId ? (
          <PathwayView
            user={user}
            pathwayId={selectedPathwayId}
            selectedTrack={selectedPathway?.selectedTrack}
            onSelectBlock={(block) => {
              setSelectedBlock(block);
              onSelectBlock(block.id, block.type);
            }}
          />
        ) : null;
      case 'block-detail':
        if (!selectedBlock || !selectedPathwayId) return null;
        if (selectedBlock.type === 'theory') {
          return (
            <TheoryBlockView
              user={user}
              block={selectedBlock}
              pathwayId={selectedPathwayId}
            />
          );
        }
        if (selectedBlock.type === 'ojt') {
          return (
            <OjtBlockView
              user={user}
              block={selectedBlock}
              pathwayId={selectedPathwayId}
              onStartSession={(sessionId) => {
                setActiveSessionId(sessionId);
                onStartOjtSession(selectedBlock.id);
              }}
            />
          );
        }
        if (selectedBlock.type === 'practical') {
          return (
            <PracticalBlockView
              user={user}
              block={selectedBlock}
              pathwayId={selectedPathwayId}
            />
          );
        }
        if (selectedBlock.type === 'assessment') {
          return (
            <AssessmentBlockView
              user={user}
              block={selectedBlock}
              pathwayId={selectedPathwayId}
            />
          );
        }
        if (selectedBlock.type === 'upload') {
          return (
            <UploadBlockView
              user={user}
              block={selectedBlock}
              pathwayId={selectedPathwayId}
            />
          );
        }
        if (selectedBlock.type === 'api') {
          return (
            <ApiBlockView
              user={user}
              block={selectedBlock}
              pathwayId={selectedPathwayId}
            />
          );
        }
        if (selectedBlock.type === 'authorisation') {
          return (
            <AuthorisationBlockView
              user={user}
              block={selectedBlock}
              pathwayId={selectedPathwayId}
            />
          );
        }
        return (
          <PlaceholderBlockView block={selectedBlock} />
        );
      case 'ojt-session':
        if (!selectedBlock || !selectedPathwayId) return null;
        if (selectedBlock.ojtConfig?.agentEnabled) {
          return (
            <OjtAgentChat
              user={user}
              block={selectedBlock}
              pathwayId={selectedPathwayId}
              sessionId={activeSessionId}
              pathwayBlocks={selectedPathway?.blocks}
              onComplete={() => onViewChange('block-detail')}
              onCancel={() => onViewChange('block-detail')}
            />
          );
        }
        return (
          <OjtSessionFlow
            user={user}
            block={selectedBlock}
            pathwayId={selectedPathwayId}
            sessionId={activeSessionId}
            pathwayBlocks={selectedPathway?.blocks}
            onComplete={() => onViewChange('block-detail')}
            onCancel={() => onViewChange('block-detail')}
          />
        );
      default:
        return null;
    }
  };

  return (
    <CandidateLayout
      user={user}
      currentView={currentView}
      onViewChange={onViewChange}
      onLogout={onLogout}
      onSwitchToAdmin={onSwitchToAdmin}
      isAdmin={isAdmin}
      showBack={showBack}
      onBack={handleBack}
      title={getTitle()}
    >
      {renderContent()}
    </CandidateLayout>
  );
};
